import {
    canContain,
    cloneWithNewIds,
    createNode,
    createSection,
    type DocNode,
    type EmailDocument,
    type StyleProps,
} from "@edm/schema";

/**
 * Pure tree operations used inside immer recipes. All operations validate
 * registry containment rules; illegal moves are no-ops.
 */

export interface NodeLocation {
    parent: DocNode;
    index: number;
}

export function locate(root: DocNode, id: string): NodeLocation | null {
    for (let i = 0; i < (root.children?.length ?? 0); i++) {
        const child = root.children![i]!;
        if (child.id === id) return { parent: root, index: i };
        const found = locate(child, id);
        if (found) return found;
    }
    return null;
}

export function getNode(root: DocNode, id: string): DocNode | null {
    if (root.id === id) return root;
    for (const child of root.children ?? []) {
        const hit = getNode(child, id);
        if (hit) return hit;
    }
    return null;
}

/** Ancestors of `id` from the root down to (and including) the node itself.
 * Empty when the id is not in the tree. */
export function pathTo(root: DocNode, id: string): DocNode[] {
    if (root.id === id) return [root];
    for (const child of root.children ?? []) {
        const sub = pathTo(child, id);
        if (sub.length) return [root, ...sub];
    }
    return [];
}

/** Can a block of `childType` end up inside a container of `parentType`?
 * Mirrors insertNode: the root also accepts content/section-level blocks
 * because insertNode auto-wraps them in a section. `allowRootWrap` is false
 * for MOVES, which have no auto-wrap path. */
function accepts(parentType: string, childType: string, allowRootWrap: boolean): boolean {
    if (canContain(parentType, childType)) return true;
    if (
        allowRootWrap &&
        parentType === "root" &&
        (canContain("column", childType) || canContain("section", childType))
    ) {
        return true;
    }
    return false;
}

export interface DropTarget {
    containerId: string;
    index: number;
}

/**
 * Decide where a dragged block should land given the droppable it was released
 * over. Walks UP the ancestor chain until it finds a container that may legally
 * hold the block, so a section dropped while hovering a paragraph deep inside
 * another section lands next to that section at the root — never silently
 * rejected. `after` places below the hovered node's midpoint. Returns null only
 * when nothing in the chain can accept the block (caller should tell the user).
 */
export function resolveDropTarget(
    root: DocNode,
    draggedType: string,
    over: { nodeId?: string; containerId?: string },
    after: boolean,
    allowRootWrap: boolean,
): DropTarget | null {
    // Released over a specific node: insert adjacent to whichever ancestor sits
    // directly inside the first container that accepts the block.
    if (over.nodeId) {
        const path = pathTo(root, over.nodeId); // [root, ..., overNode]
        for (let i = path.length - 1; i >= 1; i--) {
            const parent = path[i - 1]!;
            const child = path[i]!;
            if (accepts(parent.type, draggedType, allowRootWrap)) {
                const idx = (parent.children ?? []).findIndex((c) => c.id === child.id);
                return {
                    containerId: parent.id,
                    index: Math.max(0, idx) + (after ? 1 : 0),
                };
            }
        }
        return null;
    }
    // Released over a container droppable (root / column / hero / card): append
    // into the innermost ancestor that accepts the block.
    if (over.containerId) {
        const path = pathTo(root, over.containerId); // [root, ..., container]
        for (let i = path.length - 1; i >= 0; i--) {
            const cand = path[i]!;
            if (accepts(cand.type, draggedType, allowRootWrap)) {
                return { containerId: cand.id, index: (cand.children ?? []).length };
            }
        }
        return null;
    }
    return null;
}

/** Insert `node` into container `containerId` at `index` (append when omitted).
 * Content blocks dropped on the root are auto-wrapped in section>row>column. */
export function insertNode(
    doc: EmailDocument,
    node: DocNode,
    containerId: string,
    index?: number,
): boolean {
    const container = getNode(doc.root, containerId);
    if (!container) return false;

    if (canContain(container.type, node.type)) {
        container.children ??= [];
        container.children.splice(index ?? container.children.length, 0, node);
        return true;
    }
    // Auto-wrap: content aimed at root becomes a full section.
    if (container.type === "root" && canContain("column", node.type)) {
        const section = createSection([1]);
        section.children![0]!.children![0]!.children!.push(node);
        container.children ??= [];
        container.children.splice(index ?? container.children.length, 0, section);
        return true;
    }
    // Hero/card aimed at root get their own wrapping section.
    if (container.type === "root" && canContain("section", node.type)) {
        const section = createNode("section");
        section.children = [node];
        container.children ??= [];
        container.children.splice(index ?? container.children.length, 0, section);
        return true;
    }
    return false;
}

export function removeNode(doc: EmailDocument, id: string): DocNode | null {
    const loc = locate(doc.root, id);
    if (!loc) return null;
    const [removed] = loc.parent.children!.splice(loc.index, 1);
    return removed ?? null;
}

/** Move node `id` into `containerId` at `index`. Returns false (no change)
 * for illegal targets, including moving a node into its own subtree. */
export function moveNode(
    doc: EmailDocument,
    id: string,
    containerId: string,
    index: number,
): boolean {
    const node = getNode(doc.root, id);
    const container = getNode(doc.root, containerId);
    if (!node || !container) return false;
    if (node.locked) return false;
    if (getNode(node, containerId)) return false; // container inside node
    if (!canContain(container.type, node.type)) return false;

    const loc = locate(doc.root, id)!;
    const sameParent = loc.parent.id === containerId;
    loc.parent.children!.splice(loc.index, 1);
    let target = index;
    if (sameParent && loc.index < index) target -= 1;
    container.children ??= [];
    container.children.splice(Math.min(target, container.children.length), 0, node);
    return true;
}

export function duplicateNode(doc: EmailDocument, id: string): string | null {
    const loc = locate(doc.root, id);
    if (!loc) return null;
    const source = loc.parent.children![loc.index]!;
    const copy = cloneWithNewIds(source);
    loc.parent.children!.splice(loc.index + 1, 0, copy);
    return copy.id;
}

export function updateAttrs(
    doc: EmailDocument,
    id: string,
    attrs: Record<string, unknown>,
): void {
    const node = getNode(doc.root, id);
    if (node) Object.assign(node.attrs, attrs);
}

export function updateStyles(
    doc: EmailDocument,
    id: string,
    styles: StyleProps,
    mobile = false,
): void {
    const node = getNode(doc.root, id);
    if (!node) return;
    const target = mobile ? node.mobileStyles : node.styles;
    for (const [key, value] of Object.entries(styles)) {
        if (value === undefined) {
            delete (target as Record<string, unknown>)[key];
        } else {
            (target as Record<string, unknown>)[key] = value;
        }
    }
}

/** Create a node from a palette block type, expanding layout helpers. */
export function createFromPalette(blockType: string): DocNode {
    if (blockType === "section") return createSection([1]);
    if (blockType === "list") {
        // Preset: a paragraph pre-filled with a bullet list.
        const node = createNode("paragraph");
        node.attrs = {
            content: {
                type: "doc",
                content: [
                    {
                        type: "bulletList",
                        content: [
                            {
                                type: "listItem",
                                content: [
                                    {
                                        type: "paragraph",
                                        content: [{ type: "text", text: "First item" }],
                                    },
                                ],
                            },
                            {
                                type: "listItem",
                                content: [
                                    {
                                        type: "paragraph",
                                        content: [{ type: "text", text: "Second item" }],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        };
        return node;
    }
    if (blockType === "hero") {
        const hero = createNode("hero");
        const heading = createNode("heading");
        heading.attrs = { ...heading.attrs, level: 1 };
        heading.styles = { color: "#ffffff", textAlign: "center" };
        const button = createNode("button");
        hero.children = [heading, button];
        return hero;
    }
    if (blockType.startsWith("section:")) {
        const ratios = blockType
            .slice("section:".length)
            .split("-")
            .map((n) => Number(n));
        return createSection(ratios);
    }
    if (blockType === "row") {
        const row = createNode("row");
        row.children = [createNode("column")];
        return row;
    }
    return createNode(blockType);
}
